import React, { useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, Alert, Platform, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import pilotService from '../services/pilotService';

const TIFF_FILES = [
  { key: 'NDVI',          label: 'NDVI.tif',          desc: 'Índice de vigor vegetativo' },
  { key: 'GNDVI',         label: 'GNDVI.tif',         desc: 'Índice de clorofila' },
  { key: 'NDRE',          label: 'NDRE.tif',          desc: 'Índice red-edge' },
  { key: 'OSAVI',         label: 'OSAVI.tif',         desc: 'Índice ajustado por suelo' },
  { key: 'result_Green',  label: 'result_Green.tif',  desc: 'Banda cruda Green (560nm)' },
  { key: 'result_NIR',    label: 'result_NIR.tif',    desc: 'Banda cruda NIR (860nm)' },
];

export default function PilotMultispectralCompleteScreen({ route, navigation }) {
  const { item } = route.params;
  const [checked, setChecked]   = useState({});
  const [notes, setNotes]       = useState('');
  const [loading, setLoading]   = useState(false);

  const allChecked = TIFF_FILES.every(f => checked[f.key]);
  const toggle = (key) => setChecked(prev => ({ ...prev, [key]: !prev[key] }));

  const handleComplete = async () => {
    if (!allChecked) {
      const msg = 'Confirma que exportaste los 6 archivos antes de completar.';
      if (Platform.OS === 'web') { alert(msg); return; }
      Alert.alert('Archivos pendientes', msg);
      return;
    }
    setLoading(true);
    try {
      await pilotService.completeDroneMission(item._raw_id);
      if (Platform.OS === 'web') {
        alert('Misión completada. El equipo procesará los archivos.');
      } else {
        Alert.alert('✅ Misión completada', 'El equipo procesará los archivos TIFF.');
      }
      navigation.pop(2); // vuelve al dashboard
    } catch (e) {
      const msg = e?.response?.data?.error || 'Error al completar la misión.';
      if (Platform.OS === 'web') alert(msg);
      else Alert.alert('Error', msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 100 }}>

        {/* Info de la misión */}
        <View style={styles.section}>
          <Text style={styles.missionName}>{item.location_name || item.enterprise_name}</Text>
          <Text style={styles.missionId}>Misión #{item._mission_id}</Text>
          {(item.objectives_list || []).length > 0 && (
            <Text style={styles.missionSub}>{item.objectives_list.join(' · ')}</Text>
          )}
        </View>

        {/* Instrucciones */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>INSTRUCCIONES</Text>
          <Text style={styles.instruction}>
            1. Abre DJI Agriculture en tu PC y exporta los 6 archivos TIF del vuelo.
          </Text>
          <Text style={styles.instruction}>
            2. Marca cada archivo que ya exportaste.
          </Text>
          <Text style={styles.instruction}>
            3. Entrega los archivos al equipo de procesamiento (USB, Google Drive o el método acordado).
          </Text>
          <Text style={styles.instruction}>
            4. Presiona "Completar misión" para registrar la entrega.
          </Text>
        </View>

        {/* Checklist de archivos */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>ARCHIVOS A EXPORTAR</Text>
          {TIFF_FILES.map(f => (
            <TouchableOpacity
              key={f.key}
              style={styles.fileRow}
              onPress={() => toggle(f.key)}
              activeOpacity={0.7}
            >
              <View style={[styles.checkbox, checked[f.key] && styles.checkboxChecked]}>
                {checked[f.key] && <Ionicons name="checkmark" size={14} color="#fff" />}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[styles.fileName, checked[f.key] && styles.fileNameChecked]}>
                  {f.label}
                </Text>
                <Text style={styles.fileDesc}>{f.desc}</Text>
              </View>
            </TouchableOpacity>
          ))}
          <View style={styles.progress}>
            <View style={[styles.progressBar, { width: `${(Object.values(checked).filter(Boolean).length / TIFF_FILES.length) * 100}%` }]} />
          </View>
          <Text style={styles.progressLabel}>
            {Object.values(checked).filter(Boolean).length} / {TIFF_FILES.length} archivos
          </Text>
        </View>

        {/* Notas opcionales */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>NOTAS (OPCIONAL)</Text>
          <TextInput
            style={styles.input}
            placeholder="Ej: condiciones del vuelo, observaciones del campo..."
            placeholderTextColor="#9ca3af"
            multiline
            numberOfLines={3}
            value={notes}
            onChangeText={setNotes}
          />
        </View>

      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.completeBtn, !allChecked && styles.completeBtnDisabled]}
          onPress={handleComplete}
          disabled={loading}
        >
          {loading
            ? <ActivityIndicator color="#fff" size="small" />
            : <>
                <Ionicons name="checkmark-circle-outline" size={18} color="#fff" style={{ marginRight: 8 }} />
                <Text style={styles.completeBtnText}>Completar misión</Text>
              </>
          }
        </TouchableOpacity>
        {!allChecked && (
          <Text style={styles.hint}>Marca los 6 archivos para completar</Text>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container:          { flex: 1, backgroundColor: '#f5f7fa' },
  section:            { backgroundColor: '#fff', marginHorizontal: 12, marginTop: 12, borderRadius: 16, padding: 16 },
  sectionTitle:       { fontSize: 11, fontWeight: '700', color: '#9ca3af', letterSpacing: 0.8, marginBottom: 10 },
  missionName:        { fontSize: 18, fontWeight: '700', color: '#111827' },
  missionId:          { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  missionSub:         { fontSize: 13, color: '#6b7280', marginTop: 4 },
  instruction:        { fontSize: 13, color: '#374151', marginBottom: 8, lineHeight: 20 },
  fileRow:            { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  checkbox:           { width: 24, height: 24, borderRadius: 6, borderWidth: 2, borderColor: '#d1d5db', justifyContent: 'center', alignItems: 'center' },
  checkboxChecked:    { backgroundColor: '#059669', borderColor: '#059669' },
  fileName:           { fontSize: 14, fontWeight: '600', color: '#111827' },
  fileNameChecked:    { color: '#059669', textDecorationLine: 'line-through' },
  fileDesc:           { fontSize: 12, color: '#9ca3af', marginTop: 1 },
  progress:           { height: 4, backgroundColor: '#f3f4f6', borderRadius: 2, marginTop: 14, overflow: 'hidden' },
  progressBar:        { height: '100%', backgroundColor: '#059669', borderRadius: 2 },
  progressLabel:      { fontSize: 12, color: '#6b7280', marginTop: 6, textAlign: 'right' },
  input:              { borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, padding: 12, fontSize: 13, color: '#374151', minHeight: 80, textAlignVertical: 'top' },
  footer:             { padding: 16, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#f3f4f6' },
  completeBtn:        { backgroundColor: '#059669', borderRadius: 14, padding: 16, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' },
  completeBtnDisabled:{ backgroundColor: '#9ca3af' },
  completeBtnText:    { color: '#fff', fontWeight: '700', fontSize: 15 },
  hint:               { fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 8 },
});
